import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import VehicleTypeService from "../services/VehicleTypeService";
import VehicleHireService from "../services/vehicleHireService";
import VehicleRegistrationService from "../services/vehicleRegistrationService";
import LeaseTypeService from "../services/LeaseTypeService";
import Breadcrumb from "../components/Breadcrumb";
import StorageService from "../services/StorageService";
import MessageDialog from "../components/MessageDialog";


export default function AddVehicleHire() {
  const navigate = useNavigate();
  const [editLoading, setEditLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [editLoading, setEditLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("error");
  // const messageTimer = useRef(null);

  const [editMode, setEditMode] = useState(false);
  const [hireId, setHireId] = useState(null);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [leaseTypes, setLeaseTypes] = useState([]);
  const { id } = useParams();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const startDateValue = watch("startDate");
  const vehicleWatch = watch("vehicleId");


  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    // if (messageTimer.current) clearTimeout(messageTimer.current);
    // messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  // const closeMessage = () => {
  //   setMessage("");
  //   if (messageTimer.current) clearTimeout(messageTimer.current);
  // };

  const fetchVehicleTypes = async () => {
    try {
      const res = await VehicleTypeService.getActiveVehicleTypes();
      let data = [];
      if (Array.isArray(res)) data = res;
      else if (Array.isArray(res.data)) data = res.data;
      else if (Array.isArray(res?.data?.data)) data = res.data.data;
      else if (Array.isArray(res?.vehicleTypes)) data = res.vehicleTypes;
      setVehicleTypes(data);
    } catch (error) {
      console.error("Error fetching vehicle types:", error);
      setVehicleTypes([]);
    }
  };

  const fetchVehicles = async (vehicleTypeId) => {
    if (!vehicleTypeId) return;

    try {
      const activeCompany = StorageService.activeCompany();

      if (!activeCompany?.companyId) {
        console.error("Company ID missing");
        setVehicles([]);
        return;
      }

      const res = await VehicleRegistrationService.getByVehicleTypeId(
        vehicleTypeId,
        activeCompany.companyId
      );

      const data =
        res?.data ||
        res?.data?.data ||
        res ||
        [];

      setVehicles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      setVehicles([]);
    }
  };

  const fetchLeaseTypes = async () => {
    try {
      const res = await LeaseTypeService.getActive();
      let data = [];
      if (Array.isArray(res)) data = res;
      else if (Array.isArray(res.data)) data = res.data;
      else if (Array.isArray(res?.data?.data)) data = res.data.data;
      else if (Array.isArray(res?.leaseTypes)) data = res.leaseTypes;
      setLeaseTypes(data);
    } catch (error) {
      console.error("Error fetching lease types:", error);
      setLeaseTypes([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (id) {

        setEditLoading(true);
        setLoading(true);

        setEditMode(true);
        setHireId(id);
      }

      await Promise.all([fetchVehicleTypes(), fetchLeaseTypes()]);

      if (id) {
        await fetchVehicleHireById(id);
      } else {
        reset({ status: "Active" });
      }
    };

    init();
  }, [id]);


  // DEFAULT START DATE WHEN ADDING (NOT EDIT MODE)
  useEffect(() => {
    if (!editMode) {
      const today = new Date().toISOString().split("T")[0];
      setValue("startDate", today);
    }
  }, [editMode, setValue]);

  const fetchVehicleHireById = async (hireId) => {
    setEditLoading(true);
    setLoading(true);
    // setEditLoading(true);
    try {
      const record = await VehicleHireService.getById(hireId);

      if (!record) {
        showMessage("Failed to load Vehicle Hire", "error");
        return;
      }

      await fetchVehicles(record.vehicleTypeId);

      setValue("vehicleTypeId", record.vehicleTypeId || "");
      setValue("vehicleId", record.vehicleId || "");
      setValue("leaseTypeId", record.leaseTypeId || "");
      setValue("vehicleHirerate", record.vehicleHirerate || "");
      setValue("fuelBorneBy", record.fuelBorneBy || "");
      setValue("startDate", record.startDate?.split("T")[0] || "");
      setValue("endDate", record.endDate?.split("T")[0] || "");
      setValue("status", record.status || "Active");

    } catch (error) {
      console.error("Error fetching hire:", error);
      showMessage("Error fetching hire details", "error");
    } finally {
      setEditLoading(false);
      setLoading(false);
      // setEditLoading(false);
    }
  };

  useEffect(() => {
    if (!vehicleWatch || !vehicles.length) {
      setValue("ownership", "");
      return;
    }

    const selectedVehicle = vehicles.find(
      (v) => String(v.id) === String(vehicleWatch)
    );

    if (!selectedVehicle) {
      setValue("ownership", "");
      return;
    }

    const ownershipType = selectedVehicle.ownerShipType?.toLowerCase();

    if (ownershipType === "lease") {
      const vendorName =
        selectedVehicle.vehicleVendorCategoryName ||
        selectedVehicle.vendorCategoryName ||
        selectedVehicle.vehicleVendorName ||
        selectedVehicle.vendorName ||
        "";

      setValue(
        "ownership",
        vendorName ? `Lease - ${vendorName}` : "Lease"
      );
    } else {
      setValue("ownership", selectedVehicle.ownerShipType || "");
    }
  }, [vehicleWatch, vehicles, setValue]);


  const onSubmit = async (data) => {
    setLoading(true);
    try {
      data.updatedDate = new Date().toISOString();
      data.updatedBy = StorageService.loginData().employeeId;
      data.companyId = StorageService.activeCompany().companyId;
      data.isActive = data.status === "Active" ? 1 : 0;

      let res;
      if (editMode && hireId) {
        res = await VehicleHireService.update(hireId, data);
      } else {
        data.createdDate = new Date().toISOString();
        data.createdBy = StorageService.loginData().employeeId;
        res = await VehicleHireService.create(data);
      }

      if (res?.status || res?.id) {
        showMessage(
          editMode
            ? "Vehicle Hire updated successfully!"
            : "Vehicle Hire added successfully!",
          "success"
        );
        setTimeout(() => {
          navigate("/cpnl/vehiclehire/view");
        }, 1200);
      } else {
        showMessage("Failed to save Vehicle Hire", "error");
      }
    } catch (err) {
      console.error("Error saving vehicle hire:", err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Error saving vehicle hire.";
      showMessage(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/cpnl/vehiclehire/view");
  };

  return (
    <>
      <Breadcrumb
        pageTitle={editMode ? "Modify Vehicle Hire" : "Add Vehicle Hire"}
        secondTitle="Vehicle Management"
      />

      {/* {message && (
        <div
          className={`alert alert-${msgType === "success" ? "success" : "danger"} alert-dismissible fade show`}
          role="alert"
        >
          {message}
          <button type="button" className="btn-close" onClick={closeMessage}></button>
        </div>
      )} */}
      
        <MessageDialog
          message={message}
          type={msgType}
          setMessage={setMessage}
          setMsgType={setMsgType}
        />
      

      <div className="row">
        <div className="card py-2 bg-white shadow-sm">
          {editLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <div className="mt-2 text-muted">Loading VehicleHire details...</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} noValidate>

              {/* Row 1: Vehicle Type + Vehicle */}
              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Vehicle Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    {...register("vehicleTypeId", { required: "Vehicle Type is required" })}
                    onChange={(e) => fetchVehicles(e.target.value)}
                  >
                    <option value="">Select Vehicle Type</option>
                    {vehicleTypes.map((vt) => (
                      <option key={vt.id} value={vt.id}>
                        {vt.vehicleType}
                      </option>
                    ))}
                  </select>
                  {errors.vehicleTypeId && (
                    <small className="text-danger">{errors.vehicleTypeId.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Vehicle <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    {...register("vehicleId", { required: "Vehicle is required" })}
                    onChange={(e) => setValue("vehicleId", e.target.value)}
                  >

                    <option value="">Select Vehicle</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.vehicleName} - {v.registrationNo}
                      </option>
                    ))}
                  </select>
                  {errors.vehicleId && (
                    <small className="text-danger">{errors.vehicleId.message}</small>
                  )}
                </div>
              </div>

              {/* Row 2: Lease Type + Hire Rate */}
              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Ownership</label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("ownership")}
                    readOnly
                    disabled
                  />
                </div>


                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Lease Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    {...register("leaseTypeId", { required: "Lease Type is required" })}
                  >
                    <option value="">Select Lease Type</option>
                    {leaseTypes.map((lt) => (
                      <option key={lt.id} value={lt.id}>
                        {lt.leaseType}
                      </option>
                    ))}
                  </select>
                  {errors.leaseTypeId && (
                    <small className="text-danger">{errors.leaseTypeId.message}</small>
                  )}
                </div>
              </div>

              {/* Row 3: Start Date + End Date (UPDATED VALIDATION) */}
              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    {...register("startDate", {
                      required: "Start date is required",
                    })}
                  />
                  {errors.startDate && (
                    <small className="text-danger">{errors.startDate.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    End Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    {...register("endDate", {
                      validate: (value) => {
                        const sDate = watch("startDate");
                        if (!sDate || !value) return true;

                        return (
                          new Date(value) >= new Date(sDate) ||
                          "End date must be greater than or equal to Start date"
                        );
                      },
                    })}
                  />
                  {errors.endDate && (
                    <small className="text-danger">{errors.endDate.message}</small>
                  )}
                </div>
              </div>

              {/* Row 4: Fuel Borne By */}
              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Fuel Borne By <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    {...register("fuelBorneBy", { required: "Fuel Borne By is required" })}
                  >
                    <option value="">Select</option>
                    <option value="Company">Company</option>
                    <option value="Vendor">Vendor</option>
                  </select>
                  {errors.fuelBorneBy && (
                    <small className="text-danger">{errors.fuelBorneBy.message}</small>
                  )}
                </div>
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Vehicle Hire Rate <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    placeholder="Enter Hire Rate"
                    {...register("vehicleHirerate", { required: "Hire rate is required" })}
                  />
                  {errors.vehicleHirerate && (
                    <small className="text-danger">{errors.vehicleHirerate.message}</small>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="mt-3">
                <button type="submit" className="btn btn-primary me-2" disabled={loading}>
                  <i className="ti ti-circle-check me-1"></i>
                  {loading
                    ? editMode
                      ? "Updating..."
                      : "Saving..."
                    : editMode
                      ? "Update"
                      : "Save"}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  <i className="ti ti-cancel me-1"></i>
                  Cancel
                </button>
              </div>

            </form>
          )}
        </div>
      </div>
    </>
  );
}
